import React, { useContext, useEffect, useMemo, useState } from 'react'
import XCard, { XCARD_TYPE } from '../../xCard'
import XInput, { XInputTypeEnum } from '../../../components/XInput'
import style from './style.module.scss'
import XDropdown from '../../../components/XDropdown'
import { useTranslation } from '../../../../i18n'
import { useForm } from 'react-hook-form'
import { GENERIC_ERRORS } from '../../../constants'
import { useSendSupportEmailMutation } from '../../../../types/graphql'
import { UserContext } from '../../../context/user'
import XBack from '../../../components/XBack'
import XText from '../../../components/XText'
import { XTEXT_DECORATIONS, XTEXT_TYPE } from '../../../styles/constants'
import XColors from '../../../styles/colors'
import { isMobile } from 'react-device-detect'
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'

interface Props {
    onBack: () => void
}

const SupportEmailForm = (props: Props) => {
    const { t } = useTranslation(window.LANG_NS)
    const form = useForm()
    const { register, setError, handleSubmit, clearErrors, errors, getValues, watch } = form
    const [isReadOnly, setIsReadOnly] = useState(true)
    const [sendSupportEmail, { loading, error }] = useSendSupportEmailMutation()

    const { authenticatedUser } = useContext(UserContext)
    const charCount = watch('help_message', '').length
    const fullName = useMemo(() => {
        return authenticatedUser
            ? `${authenticatedUser?.person?.profile?.firstName} ${authenticatedUser?.person?.profile?.lastName}`
            : null
    }, [authenticatedUser])

    useEffect(() => {
        if (charCount > 500) {
            setError('help_message', { type: 'invalid', message: t('error_max_length_500') })
        } else {
            clearErrors('help_message')
        }
    }, [charCount])

    const dropdownKey = useMemo(() => {
        return [
            'replace_lost_stolen_card',
            'change_contact_information',
            'report_payment_fraud',
            'request_letter_pilot',
            'scheduled_payment_question',
            'trouble_accessing_account',
            'ethnicity_option_other',
        ]
    }, [])

    const dropDownData = useMemo(() => dropdownKey.map((i) => t(i)), [t])
    const onSubmit = (values) => {
        const categoryKey = dropdownKey.find((key) => t(key) === values.categories)
        const userId = authenticatedUser ? atob(authenticatedUser?.id).split(':')[1] : null
        sendSupportEmail({
            variables: {
                userMessage: values.help_message,
                userName: values.support_user_name,
                userEmail: values.email,
                userCategory: t(categoryKey, { lng: 'en' }),
                userId: userId,
            },
        }).then(() => {
            props.onBack()
        })
    }

    return (
        <div className={style.supportEmail}>
            {!isMobile && <XBack onClick={props.onBack} />}
            <XCard
                backButton={isMobile ? t('back') : null}
                backButtonAction={isMobile ? props.onBack : null}
                captchaValidation={isReadOnly}
                title={t('help_email_title')}
                continueButton={t('send_button')}
                continueAction={handleSubmit(onSubmit)}
                type={XCARD_TYPE.EMAIL}
                form={form}
                loading={loading}
            >
                <div className={style.supportEmail__container}>
                    <XInput
                        id={'support_user_name'}
                        name={'support_user_name'}
                        type={XInputTypeEnum.TEXT}
                        label={t('home_table_header_full_name')}
                        initialValue={fullName}
                        ref={register({
                            required: GENERIC_ERRORS.REQUIRED_FIELD,
                            minLength: {
                                value: 2,
                                message: 'error_name_at_least_two_characters',
                            },
                            maxLength: {
                                value: 50,
                                message: 'error_max_length_50',
                            },
                        })}
                    />
                    <XDropdown
                        id={'categories'}
                        name={'categories'}
                        label={t('email_support_help_label')}
                        dropdownData={dropDownData}
                    />
                </div>
                <XInput
                    type={XInputTypeEnum.EMAIL}
                    id={'email'}
                    name={'email'}
                    initialValue={authenticatedUser?.email || null}
                    label={t('email_support_email_label')}
                    ref={register({
                        required: GENERIC_ERRORS.REQUIRED_FIELD,
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                            message: 'INVALID_EMAIL_ADDRESS',
                        },
                    })}
                />
                <XInput
                    type={XInputTypeEnum.LARGE_TEXT}
                    id={'help_message'}
                    name={'help_message'}
                    label={t('email_support_message_label')}
                    ref={register({
                        required: GENERIC_ERRORS.REQUIRED_FIELD,
                        maxLength: {
                            value: 500,
                            message: 'error_max_length_500',
                        },
                    })}
                />
                <XText type={XTEXT_TYPE.LABEL} color={XColors.backgroundColor} decorator={XTEXT_DECORATIONS.BOLD}>
                    {t('character_count')} {charCount}/500
                </XText>
            </XCard>
            <GoogleReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}>
                <GoogleReCaptcha
                    onVerify={() => {
                        setIsReadOnly(false)
                    }}
                />
            </GoogleReCaptchaProvider>
        </div>
    )
}

export default SupportEmailForm
